import { Check } from "lucide-react";
import {
  useId,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/ui/page-header";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { useLawFirms } from "../../../features/lawFirms/context/LawFirmsContext";
import {
  CreateLawFirmRequest,
  FirmSize,
  Source,
  SubscriptionPlanType
} from "../../../api/types/lawFirm";

const US_STATE_ABBREVIATIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
] as const;

const FIRM_SIZES = Object.values(FirmSize);
const SOURCES = Object.values(Source);

const PLAN_SUBTITLE: Partial<Record<SubscriptionPlanType, string>> = {
  [SubscriptionPlanType.FREE_TRIAL]: "Trial access for new firms",
  [SubscriptionPlanType.LITE]:
    "Upload & Organize, Assemble Exhibits, and Download Materials only",
  [SubscriptionPlanType.SOLO_SMALL_FIRM]:
    "Best for solo practitioners and small teams",
  [SubscriptionPlanType.TIER_ONE]: "Entry-level tier",
  [SubscriptionPlanType.TIER_TWO]: "Mid-level tier",
  [SubscriptionPlanType.TIER_THREE]: "Top-tier plan",
  [SubscriptionPlanType.LEGACY]: "Historical plan type (Tier 1, 2, 3)"
};

const TextInput = ({
  label,
  className,
  ...props
}: ComponentProps<typeof Input> & { label: string }) => {
  const id = useId();
  return (
    <div className={cn("flex min-w-0 flex-1 flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
};

const ChoiceInput = ({
  label,
  placeholder,
  value,
  options,
  onValueChange,
  className
}: {
  label: string;
  placeholder: string;
  value: string | undefined;
  options: readonly string[];
  onValueChange: (value: string) => void;
  className?: string;
}) => (
  <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
    <Label>{label}</Label>
    <Select value={value || undefined} onValueChange={onValueChange}>
      <SelectTrigger className="w-full" aria-label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const FormSection = ({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children: ReactNode;
}) => (
  <section className="flex w-full flex-col gap-3">
    <SectionHeader title={title} description={description} />
    <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
      {children}
    </div>
  </section>
);

type Props = {
  variant: "page" | "drawer";
  onCancel: () => void;
  onCreated: (lawFirmUid: string) => void;
};

export function ProvisionNewFirmForm({ variant, onCancel, onCreated }: Props) {
  const { createLawFirm, isSaving } = useLawFirms();
  const [name, setName] = useState("");
  const [creditBalance, setCreditBalance] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [firmSize, setFirmSize] = useState<FirmSize | undefined>(undefined);
  const [source, setSource] = useState<Source | undefined>(undefined);
  const [planType, setPlanType] = useState<SubscriptionPlanType>(
    SubscriptionPlanType.FREE_TRIAL
  );

  const parsedCredits =
    creditBalance.trim() === "" ? null : Number(creditBalance);
  const isCreditBalanceValid =
    parsedCredits !== null && Number.isFinite(parsedCredits);

  const canSubmit =
    Boolean(name.trim()) &&
    Boolean(planType) &&
    isCreditBalanceValid &&
    !isSaving;

  const payload = useMemo((): CreateLawFirmRequest | null => {
    const p: CreateLawFirmRequest = { name: name.trim() };
    const parsed =
      creditBalance.trim() === "" ? null : Number(creditBalance.trim());
    if (parsed === null || !Number.isFinite(parsed)) return null;
    // The backend adds initial plan credits onto the firm's stored balance,
    // so seed the firm at 0 and let the initial subscription credit allowance
    // establish the requested starting credits exactly once.
    p.credit_balance = 0;
    if (address.trim()) p.address = address.trim();
    if (city.trim()) p.city = city.trim();
    if (state.trim()) p.state = state.trim();
    if (zipcode.trim()) p.zipcode = zipcode.trim();
    if (phone.trim()) p.phone = phone.trim();
    if (website.trim()) p.website = website.trim();
    if (firmSize) p.firm_size = firmSize;
    if (source) p.source = source;
    if (!p.name) return null;
    if (!planType) return null;

    // Initialize a subscription (and plan) in the same create call.
    p.initial_subscription = {
      plan: {
        subscription_plan_type: planType,
        credit_allowance: parsed
      }
    };

    return p;
  }, [
    address,
    city,
    creditBalance,
    firmSize,
    name,
    phone,
    planType,
    source,
    state,
    website,
    zipcode
  ]);

  const handleCreate = async () => {
    if (!payload) return;
    const created = await createLawFirm(payload);
    onCreated(created.uid);
  };

  const plans =
    variant === "drawer"
      ? [
          SubscriptionPlanType.FREE_TRIAL,
          SubscriptionPlanType.LITE,
          SubscriptionPlanType.SOLO_SMALL_FIRM,
          SubscriptionPlanType.LEGACY
        ]
      : [
          SubscriptionPlanType.FREE_TRIAL,
          SubscriptionPlanType.LITE,
          SubscriptionPlanType.SOLO_SMALL_FIRM,
          SubscriptionPlanType.TIER_ONE,
          SubscriptionPlanType.TIER_TWO,
          SubscriptionPlanType.TIER_THREE,
          SubscriptionPlanType.LEGACY
        ];

  const sections = (
    <>
      <FormSection
        title="Firm Information"
        description="Basic details about the law firm"
      >
        <div className="flex gap-3 max-sm:flex-col">
          <TextInput
            label="Law Firm Name *"
            placeholder="Enter firm name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <ChoiceInput
            className="w-56 max-sm:w-full"
            label="Firm Size"
            placeholder="Select firm size"
            value={firmSize}
            options={FIRM_SIZES}
            onValueChange={(v) => setFirmSize(v as FirmSize)}
          />
        </div>
        <div className="flex gap-3 max-sm:flex-col">
          <TextInput
            label="Website"
            placeholder="https://firmwebsite.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          <TextInput
            label="Phone"
            placeholder="+1 (555) 000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <TextInput
          label="Address"
          placeholder="Street address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        <div className="flex gap-3 max-sm:flex-col">
          <TextInput
            label="City"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <ChoiceInput
            className="w-28 max-sm:w-full"
            label="State"
            placeholder="State"
            value={state}
            options={US_STATE_ABBREVIATIONS}
            onValueChange={setState}
          />
          <TextInput
            label="ZIP Code"
            placeholder="94105"
            value={zipcode}
            onChange={(e) => setZipcode(e.target.value)}
          />
        </div>
      </FormSection>

      <FormSection
        title="Subscription Plan"
        description="Choose the plan that best fits the firm's needs"
      >
        <RadioGroup
          value={planType}
          onValueChange={(v) => setPlanType(v as SubscriptionPlanType)}
          className="gap-2"
        >
          {plans.map((plan) => {
            const id = `plan-${plan}`;
            const selected = plan === planType;
            return (
              <label
                key={plan}
                htmlFor={id}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 ring-1 ring-line-2 hover:bg-fill-1",
                  selected &&
                    "bg-accent-soft ring-accent-line hover:bg-accent-soft"
                )}
              >
                <RadioGroupItem id={id} value={plan} className="mt-0.5" />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-base font-medium text-fg">{plan}</span>
                  {PLAN_SUBTITLE[plan] ? (
                    <span className="text-sm text-fg-3">
                      {PLAN_SUBTITLE[plan]}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </RadioGroup>
      </FormSection>

      <FormSection
        title="Credits"
        description="Starting credits to provision for the firm"
      >
        <TextInput
          className="max-w-60"
          label="Credit Balance *"
          placeholder="0"
          inputMode="numeric"
          value={creditBalance}
          onChange={(e) => setCreditBalance(e.target.value)}
        />
      </FormSection>

      <FormSection
        title="Additional Details"
        description="Optional metadata about the firm"
      >
        <ChoiceInput
          label="Source"
          placeholder="Select source"
          value={source}
          options={SOURCES}
          onValueChange={(v) => setSource(v as Source)}
        />
      </FormSection>
    </>
  );

  const actions = (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={onCancel}
        disabled={isSaving}
      >
        Cancel
      </Button>
      <Button size="sm" disabled={!canSubmit} onClick={handleCreate}>
        {isSaving ? <Spinner /> : <Check />}
        Create Firm
      </Button>
    </>
  );

  if (variant === "page") {
    return (
      <div className="flex w-full items-start gap-6 max-md:flex-col">
        <div className="flex min-w-0 flex-1 flex-col gap-8">{sections}</div>
        <aside className="sticky top-0 flex w-80 shrink-0 flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2 max-md:w-full">
          <div className="flex items-center justify-between">
            <span className="text-base font-medium text-fg">
              Provisioning Summary
            </span>
            <StatusBadge tone="accent">{planType}</StatusBadge>
          </div>
          <dl className="flex flex-col gap-2 text-base">
            <div className="flex justify-between gap-3">
              <dt className="text-fg-3">Firm Name</dt>
              <dd className="truncate text-fg">{name.trim() || "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-fg-3">Credits</dt>
              <dd className="text-fg tabular-nums">
                {creditBalance === "" ? "—" : creditBalance}
              </dd>
            </div>
          </dl>
          <p className="text-sm text-fg-3">
            The firm will receive login credentials via email once provisioning
            is complete. Billing will begin immediately unless a trial period is
            selected.
          </p>
          <div className="flex justify-end gap-2">{actions}</div>
        </aside>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-stretch">
      <div className="flex w-full flex-col gap-8 px-4 py-4 pb-24">
        {sections}
      </div>
      <div className="sticky bottom-0 flex w-full items-center justify-end gap-2 border-t border-line-1 bg-elevated px-4 py-3">
        {actions}
      </div>
    </div>
  );
}
